import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "../UsmLayout";
import HubTabs from "../hub/HubTabs";
import Can from "@/components/usm/Can";
import { toast } from "sonner";
import { Plus, Pencil, Power, PowerOff, Shield, KeyRound, Archive } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Süper Yönetici" },
  { value: "branch_manager", label: "Şube Müdürü" },
  { value: "sales_employee", label: "Satış Personeli" },
];

const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));

const EMPTY = {
  username: "", password: "", full_name: "", email: "", phone: "",
  role: "sales_employee", branch_id: "", is_active: true,
  permissions_override: [], permissions_deny: [],
};

export default function EmployeesList() {
  const { isSuperAdmin, hasPerm, employee: currentEmp } = useUsmAuth();
  // Phase 54.7.B — same authorisation shape as the Backup module:
  // only usernames "admin" and "cemil" may archive employees.
  const canArchive = useMemo(() => {
    const u = (currentEmp?.username || "").toLowerCase();
    return u === "admin" || u === "cemil";
  }, [currentEmp?.username]);

  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filter, setFilter] = useState({ q: "", role: "", branch_id: "" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [permissionsFor, setPermissionsFor] = useState(null);
  const [pwResetFor, setPwResetFor] = useState(null);
  const [archiveFor, setArchiveFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.q) params.set("q", filter.q);
      if (filter.role) params.set("role", filter.role);
      if (filter.branch_id) params.set("branch_id", filter.branch_id);
      const [emps, brs] = await Promise.all([
        usmApi.get(`/employees${params.toString() ? `?${params.toString()}` : ""}`),
        usmApi.get("/branches"),
      ]);
      setItems(emps.data || []);
      setBranches(brs.data || []);
    } catch (e) { toast.error("Çalışanlar yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.q, filter.role, filter.branch_id]);

  const onSave = async (data, mode, id) => {
    try {
      if (mode === "create") await usmApi.post("/employees", data);
      else await usmApi.patch(`/employees/${id}`, data);
      toast.success("Kaydedildi");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt başarısız"); }
  };

  const toggleActive = async (emp) => {
    try {
      await usmApi.post(`/employees/${emp.id}/${emp.is_active ? "deactivate" : "activate"}`);
      toast.success("Güncellendi");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const branchById = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);

  return (
    <div data-testid="usm-employees">
      <HubTabs section="branches" />
      <PageHeader
        title="Çalışanlar"
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Çalışanlar" }]}
        actions={
          canArchive && (
            <button data-testid="employee-new-btn" onClick={() => setEditing({ mode: "create", data: EMPTY })}
              className="bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 inline-flex items-center gap-2">
              <Plus size={14} /> Yeni Çalışan
            </button>
          )
        }
      />

      <div className="bg-white border border-[#E7E5E4] mb-4 p-3 flex flex-wrap gap-3">
        <input data-testid="employees-search" placeholder="Ara: kullanıcı adı, ad veya e-posta..." value={filter.q}
          onChange={(e) => setFilter((p) => ({ ...p, q: e.target.value }))}
          className="flex-1 min-w-[200px] px-3 py-2 border border-[#E7E5E4] text-sm" />
        <select value={filter.role} onChange={(e) => setFilter((p) => ({ ...p, role: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm">
          <option value="">Tüm roller</option>
          {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {isSuperAdmin && (
          <select value={filter.branch_id} onChange={(e) => setFilter((p) => ({ ...p, branch_id: e.target.value }))} className="px-3 py-2 border border-[#E7E5E4] text-sm">
            <option value="">Tüm şubeler</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="text-left px-4 py-3">Kullanıcı</th>
              <th className="text-left px-4 py-3">Ad</th>
              <th className="text-left px-4 py-3">Rol</th>
              <th className="text-left px-4 py-3">Şube</th>
              <th className="text-left px-4 py-3">Durum</th>
              <th className="text-right px-4 py-3 w-40">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[#71717A]">Yükleniyor...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717A]">Çalışan bulunamadı</td></tr>
            ) : items.map((e) => (
              <tr key={e.id} data-testid={`employee-row-${e.username}`} className="border-t border-[#F5F5F4]">
                <td className="px-4 py-3 font-mono text-[12px]">{e.username}</td>
                <td className="px-4 py-3">{e.full_name}</td>
                <td className="px-4 py-3 text-[#52525B]">{ROLE_LABEL[e.role] || e.role}</td>
                <td className="px-4 py-3 text-[#52525B]">{e.branch_id ? (branchById[e.branch_id]?.name || "—") : "—"}</td>
                <td className="px-4 py-3">
                  {e.is_active
                    ? <span className="text-[11px] uppercase tracking-[0.15em] text-emerald-700">● Aktif</span>
                    : <span className="text-[11px] uppercase tracking-[0.15em] text-[#71717A]">● Pasif</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {canArchive && (
                    <>
                      <button data-testid={`employee-edit-${e.username}`} onClick={() => setEditing({ mode: "edit", data: e })} className="p-2 hover:bg-[#FAFAF9]" title="Düzenle"><Pencil size={14} /></button>
                      <button data-testid={`employee-pwreset-${e.username}`} onClick={() => setPwResetFor(e)} className="p-2 hover:bg-[#FAFAF9]" title="Şifre Sıfırla"><KeyRound size={14} /></button>
                      <button data-testid={`employee-perms-${e.username}`} onClick={() => setPermissionsFor(e)} className="p-2 hover:bg-[#FAFAF9]" title="İzinler"><Shield size={14} /></button>
                      <button data-testid={`employee-toggle-${e.username}`} onClick={() => toggleActive(e)} className="p-2 hover:bg-[#FAFAF9]" title={e.is_active ? "Devre dışı bırak" : "Aktifleştir"}>
                        {e.is_active ? <PowerOff size={14} /> : <Power size={14} />}
                      </button>
                      {e.id !== currentEmp?.id && (
                        <button
                          data-testid={`employee-archive-${e.username}`}
                          onClick={() => setArchiveFor(e)}
                          className="p-2 hover:bg-red-50 text-red-600"
                          title="Arşivle (sil)"
                        ><Archive size={14} /></button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EmployeeEditor branches={branches} mode={editing.mode} data={editing.data}
        onClose={() => setEditing(null)} onSave={(d) => onSave(d, editing.mode, editing.data?.id)} />}
      {permissionsFor && <PermissionsEditor employee={permissionsFor} onClose={() => setPermissionsFor(null)} onSaved={load} />}
      {pwResetFor && <PasswordResetDialog employee={pwResetFor} onClose={() => setPwResetFor(null)} />}
      {archiveFor && <ArchiveConfirmDialog employee={archiveFor} onClose={() => setArchiveFor(null)} onDone={load} />}
    </div>
  );
}

function EmployeeEditor({ branches, mode, data, onClose, onSave }) {
  const { isSuperAdmin, employee } = useUsmAuth();
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...data,
    password: "",
    branch_id: data?.branch_id || (isSuperAdmin ? "" : employee?.branch_id || ""),
  }));
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const valid = useMemo(() => {
    if (!form.full_name) return false;
    if (mode === "create") {
      if (!form.username || form.username.length < 3) return false;
      if (!form.password || form.password.length < 8) return false;
    }
    if (form.role !== "super_admin" && !form.branch_id) return false;
    return true;
  }, [form, mode]);
  const submit = () => {
    const payload = { ...form };
    if (mode === "edit") {
      delete payload.password;
      delete payload.username;
      // Branch managers cannot move people between branches or change roles
      if (!isSuperAdmin) { delete payload.role; delete payload.branch_id; }
    } else if (!payload.email) {
      delete payload.email;
    }
    onSave(payload);
  };

  return (
    <Modal title={mode === "create" ? "Yeni Çalışan" : "Çalışanı Düzenle"} onClose={onClose} testid="employee-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {mode === "create" && <>
          <Field label="Kullanıcı Adı *"><input data-testid="emp-username" value={form.username} onChange={(e) => set("username", e.target.value.toLowerCase())} className={inputCls} autoComplete="off" /></Field>
          <Field label="Şifre *"><input data-testid="emp-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} autoComplete="new-password" /></Field>
        </>}
        <Field label="Ad Soyad *"><input data-testid="emp-fullname" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} /></Field>
        <Field label="E-posta"><input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} autoComplete="off" /></Field>
        <Field label="Telefon"><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></Field>
        <Field label="Rol *">
          <select data-testid="emp-role" value={form.role} onChange={(e) => set("role", e.target.value)} disabled={!isSuperAdmin && mode === "edit"} className={inputCls}>
            {ROLE_OPTIONS.filter((r) => isSuperAdmin || r.value === "sales_employee").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        {form.role !== "super_admin" && (
          <Field label="Şube *">
            <select data-testid="emp-branch" value={form.branch_id} onChange={(e) => set("branch_id", e.target.value)} disabled={!isSuperAdmin && mode === "edit"} className={inputCls}>
              <option value="">Seçin...</option>
              {branches.filter((b) => b.is_active).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
        )}
      </div>
      <ModalFooter onClose={onClose} onSave={submit} disabled={!valid} />
    </Modal>
  );
}

function PermissionsEditor({ employee, onClose, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [override, setOverride] = useState(employee.permissions_override || []);
  const [deny, setDeny] = useState(employee.permissions_deny || []);

  useEffect(() => { usmApi.get("/employees/permissions-catalog").then((r) => setCatalog(r.data)); }, []);

  const roleDefaults = useMemo(() => new Set(catalog?.role_defaults?.[employee.role] || []), [catalog, employee.role]);

  const stateOf = (p) => {
    if (deny.includes(p)) return "deny";
    if (override.includes(p) || roleDefaults.has(p)) return "grant";
    return "no";
  };
  const setState = (p, st) => {
    setOverride((prev) => {
      const set = new Set(prev);
      if (st === "grant" && !roleDefaults.has(p)) set.add(p); else if (roleDefaults.has(p) || st !== "grant") set.delete(p);
      return [...set];
    });
    setDeny((prev) => {
      const set = new Set(prev);
      if (st === "deny" && roleDefaults.has(p)) set.add(p); else set.delete(p);
      return [...set];
    });
  };

  const save = async () => {
    try {
      await usmApi.patch(`/employees/${employee.id}/permissions`, { permissions_override: override, permissions_deny: deny });
      toast.success("İzinler güncellendi");
      onClose(); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "İzin kaydı başarısız"); }
  };

  // Group permissions by prefix
  const grouped = useMemo(() => {
    if (!catalog) return [];
    const g = {};
    for (const p of catalog.all_permissions) {
      const k = p.split(".")[0];
      g[k] = g[k] || [];
      g[k].push(p);
    }
    return Object.entries(g);
  }, [catalog]);

  return (
    <Modal title={`İzinler — ${employee.full_name}`} onClose={onClose} testid="permissions-editor" wide>
      {!catalog ? (
        <p className="text-sm text-[#71717A]">Yükleniyor...</p>
      ) : (
        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          <p className="text-xs text-[#52525B] bg-[#FAFAF9] border-l-2 border-[#C8A96A] px-3 py-2">
            <strong>{ROLE_LABEL[employee.role]}</strong> rolünün varsayılan izinleri otomatik atanır. Bireysel olarak ek izin verebilir veya bir izni reddedebilirsiniz.
          </p>
          {grouped.map(([group, perms]) => (
            <div key={group}>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#C8A96A] mb-2">{PERM_GROUP_LABEL_TR[group] || group}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {perms.map((p) => {
                  const st = stateOf(p);
                  return (
                    <div key={p} data-testid={`perm-${p}`} className="flex items-center justify-between px-3 py-2 border border-[#F5F5F4] hover:bg-[#FAFAF9]">
                      <div className="flex flex-col min-w-0">
                        <span className="text-[13px] text-[#0A0A0A] truncate">{PERM_LABEL_TR[p] || p}</span>
                        <code className="text-[10px] font-mono text-[#A1A1AA] truncate">{p}</code>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] uppercase shrink-0">
                        <PermPill active={st === "grant"} onClick={() => setState(p, "grant")} cls="bg-emerald-50 border-emerald-300 text-emerald-700">İzin</PermPill>
                        <PermPill active={st === "no"} onClick={() => setState(p, "no")} cls="bg-[#FAFAF9] border-[#E7E5E4] text-[#52525B]">Yok</PermPill>
                        <PermPill active={st === "deny"} onClick={() => setState(p, "deny")} cls="bg-red-50 border-red-300 text-red-700">Reddet</PermPill>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <ModalFooter onClose={onClose} onSave={save} disabled={!catalog} />
    </Modal>
  );
}

// Phase 50 #6 — Turkish labels for permission keys. Backend keys stay
// exactly the same; only the UI labels change. Missing entries fall back
// to the raw key so newly-added permissions never disappear.
const PERM_LABEL_TR = {
  "audit.export": "Denetim Kayıtlarını Dışa Aktar",
  "audit.view": "Denetim Kayıtlarını Görüntüle",
  "barcodes.print": "Barkod Yazdır",
  "branches.manage": "Şubeleri Yönet",
  "branches.view": "Şubeleri Görüntüle",
  "cash_close.create": "Kasa Kapanışı Oluştur",
  "cash_close.view": "Kasa Kapanışlarını Görüntüle",
  "employees.create": "Çalışan Oluştur",
  "employees.deactivate": "Çalışan Pasifleştir",
  "employees.edit": "Çalışan Düzenle",
  "employees.permissions": "Çalışan İzinlerini Yönet",
  "employees.view": "Çalışanları Görüntüle",
  "inventory.change_status": "Envanter Durumunu Değiştir",
  "inventory.create": "Envanter Ekle",
  "inventory.delete": "Envanterden Sil",
  "inventory.edit": "Envanteri Düzenle",
  "inventory.scan": "Envanter Sayım / Tarama",
  "inventory.transfer": "Envanter Transferi",
  "inventory.view": "Envanteri Görüntüle",
  "pos.access": "Ürün Satış Ekranına Erişim",
  "price_lookup.use": "Fiyat Sorgulama",
  "pricing.edit_markup": "Kâr Marjı Düzenle",
  "pricing.edit_sale": "Satış Fiyatı Düzenle",
  "pricing.view_cost": "Alış Fiyatlarını Görüntüle",
  "pricing.view_sale": "Satış Fiyatlarını Görüntüle",
  "reports.export": "Raporları Dışa Aktar",
  "reports.view_branch": "Şube Raporlarını Görüntüle",
  "reports.view_company": "Şirket Raporlarını Görüntüle",
  "reservations.approve": "Rezervasyon Onayı",
  "reservations.create": "Rezervasyon Oluştur",
  "reservations.release": "Rezervasyon İptal",
  "reservations.view": "Rezervasyonları Görüntüle",
  "sales.manage": "Satışları Yönet",
  "sales.view": "Satışları Görüntüle",
  "settings.edit": "Ayarları Düzenle",
  "settings.view": "Ayarları Görüntüle",
  "transfers.create": "Transfer Oluştur",
  "transfers.manage": "Transferleri Yönet",
  "transfers.view": "Transferleri Görüntüle",
};
const PERM_GROUP_LABEL_TR = {
  audit: "Denetim",
  barcodes: "Barkod",
  branches: "Şubeler",
  cash_close: "Kasa Kapanışı",
  employees: "Çalışanlar",
  inventory: "Envanter",
  pos: "Ürün Satış",
  price_lookup: "Fiyat Sorgulama",
  pricing: "Fiyatlandırma",
  reports: "Raporlar",
  reservations: "Rezervasyon",
  sales: "Satışlar",
  settings: "Ayarlar",
  transfers: "Transferler",
};

function PermPill({ active, onClick, cls, children }) {
  return (
    <button onClick={onClick} className={"px-2 py-0.5 border " + (active ? cls + " font-semibold" : "border-[#E7E5E4] text-[#A1A1AA] hover:bg-[#FAFAF9]")}>
      {children}
    </button>
  );
}

function PasswordResetDialog({ employee, onClose }) {
  const [pw, setPw] = useState("");
  const reset = async () => {
    try {
      await usmApi.post(`/employees/${employee.id}/reset-password`, { new_password: pw });
      toast.success("Şifre güncellendi");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Şifre sıfırlama başarısız"); }
  };
  return (
    <Modal title={`Şifre Sıfırla — ${employee.username}`} onClose={onClose} testid="pwreset-dialog">
      <Field label="Yeni Şifre (en az 8 karakter)">
        <input data-testid="pwreset-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} autoFocus />
      </Field>
      <ModalFooter onClose={onClose} onSave={reset} disabled={pw.length < 8} saveLabel="Sıfırla" />
    </Modal>
  );
}

function ArchiveConfirmDialog({ employee, onClose, onDone }) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const expected = "SIL";
  const submit = async () => {
    setBusy(true);
    try {
      await usmApi.delete(`/employees/${employee.id}`);
      toast.success(`${employee.full_name} arşivlendi. Kullanıcı adı serbest bırakıldı.`);
      onClose();
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d || "Arşivleme başarısız");
    } finally { setBusy(false); }
  };
  return (
    <Modal title={`Çalışanı Arşivle — ${employee.full_name}`} onClose={onClose} testid="employee-archive-dialog">
      <div className="space-y-4">
        <div className="text-sm text-[#52525B] bg-red-50 border-l-2 border-red-400 px-3 py-2 space-y-1">
          <p><strong>{employee.username}</strong> kullanıcısı arşivlenecek.</p>
          <ul className="list-disc pl-5 text-[13px] space-y-0.5">
            <li>Kullanıcı giriş yapamaz olacak.</li>
            <li>Geçmiş satış, muhasebe ve denetim kayıtları etkilenmez.</li>
            <li>Kullanıcı adı serbest kalır, yeni bir çalışan aynı adı kullanabilir.</li>
            <li>Bu işlem denetim kaydına yazılır ve geri alınamaz.</li>
          </ul>
        </div>
        <Field label={`Onaylamak için "${expected}" yazın`}>
          <input
            data-testid="employee-archive-confirm"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            className={inputCls}
            autoFocus
          />
        </Field>
      </div>
      <ModalFooter onClose={onClose} onSave={submit} disabled={busy || phrase.trim().toUpperCase() !== expected} saveLabel={busy ? "Arşivleniyor..." : "Arşivle"} />
    </Modal>
  );
}

function Modal({ title, onClose, children, wide = false, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div data-testid={testid} className={"bg-white w-full " + (wide ? "max-w-4xl" : "max-w-2xl") + " max-h-[90vh] overflow-y-auto"} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <h2 className="font-heading text-xl">{title}</h2>
          <button onClick={onClose} className="text-[#71717A] text-lg leading-none">✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ onClose, onSave, disabled, saveLabel = "Kaydet" }) {
  return (
    <div className="-mx-6 -mb-6 px-6 py-4 border-t border-[#E7E5E4] flex justify-end gap-3 mt-6">
      <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-[#FAFAF9]">İptal</button>
      <button data-testid="modal-save-btn" disabled={disabled} onClick={onSave} className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3">{saveLabel}</button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";
